import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchHospitalBedDaysDay,
  fetchHospitalBedDaysMonth,
  fetchHospitalBedDaysYear,
  fetchHospitalBedProfileDay,
  fetchHospitalBedProfileMonth,
  fetchHospitalBedProfileYear,
  fetchHospitalBootstrap,
  fetchHospitalCasesDay,
  fetchHospitalCasesMonth,
  fetchHospitalDepartments,
  fetchHospitalDoctorDay,
  fetchHospitalDoctorMonth,
  fetchHospitalDoctors,
  fetchHospitalMkb10Day,
  fetchHospitalMkb10Month,
  fetchHospitalMkb10Year,
  fetchHospitalOutcomeYear,
  fetchHospitalYearFunding,
} from "../api";
import type {
  HospitalDashboardState,
  HospitalFilters,
  HospitalOrg,
  HospitalPlace,
} from "../types";
import {
  createInitialHospitalChartsState,
  createInitialHospitalDashboardState,
  createInitialHospitalErrorState,
  createInitialHospitalFilters,
  createInitialHospitalLoadingState,
} from "../types";
import {
  MOTHER_FIXED_FINANCE_SOURCE_ID,
  MOTHER_FIXED_PLACE,
} from "../motherProfiles";

type HospitalFilterKey = keyof HospitalFilters;
type HospitalChartKey = keyof HospitalDashboardState["charts"];
type HospitalLoadingKey = keyof HospitalDashboardState["loading"];
type HospitalErrorKey = keyof HospitalDashboardState["errors"];
type HospitalDashboardMode = "standard" | "mother";

function toErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return "Не удалось загрузить данные";
}

function getDefaultMisId(orgs: HospitalOrg[]): string | null {
  return orgs.length > 0 ? orgs[0].mis_id : null;
}

const INITIAL_RECENT_YEARS = 4;

function getLatestFundingYear(rows: { year: number }[]): number | null {
  if (!rows.length) return null;
  return rows.reduce((max, row) => Math.max(max, row.year), rows[0].year);
}

function getInitialHospitalFiltersFromUrl(mode: HospitalDashboardMode): HospitalFilters {
  const initial = createInitialHospitalFilters();
  if (typeof window === "undefined") return initial;

  const params = new URLSearchParams(window.location.search || "");
  const mis_id = String(params.get("mis_id") || "").trim();
  const placeRaw = String(params.get("place") || "").trim().toUpperCase();
  const place: HospitalPlace | null =
    placeRaw === "\u041a\u0421" || placeRaw === "\u0414\u0421" ? (placeRaw as HospitalPlace) : null;

  if (mode === "mother") {
    return {
      ...initial,
      mis_id: mis_id || null,
      place: MOTHER_FIXED_PLACE,
      finance_source_id: MOTHER_FIXED_FINANCE_SOURCE_ID,
    };
  }

  return {
    ...initial,
    mis_id: mis_id || null,
    place,
  };
}

function normalizeHospitalFiltersForMode(
  filters: HospitalFilters,
  mode: HospitalDashboardMode,
): HospitalFilters {
  if (mode !== "mother") return filters;
  return {
    ...filters,
    place: MOTHER_FIXED_PLACE,
    finance_source_id: MOTHER_FIXED_FINANCE_SOURCE_ID,
  };
}

function getStoredDashboardRole(): string {
  try {
    return String(localStorage.getItem("dashboards_role") || "")
      .trim()
      .toUpperCase();
  } catch {
    return "";
  }
}

export interface UseHospitalDashboardResult {
  state: HospitalDashboardState;
  filters: HospitalFilters;
  charts: HospitalDashboardState["charts"];
  loading: HospitalDashboardState["loading"];
  errors: HospitalDashboardState["errors"];
  setFilter: <K extends HospitalFilterKey>(key: K, value: HospitalFilters[K]) => void;
  patchFilters: (patch: Partial<HospitalFilters>) => void;
  resetFilters: () => void;
  resetFiltersAndReload: () => Promise<void>;
  reloadBootstrap: () => Promise<void>;
  reloadAllVisibleData: (override?: Partial<HospitalFilters>) => Promise<void>;
  onSelectMisId: (mis_id: string) => Promise<void>;
  onSelectPlace: (place: HospitalPlace) => Promise<void>;
  onSelectFinanceSource: (finance_source_id: number | null) => Promise<void>;
  onSelectYearFunding: (params: { year: number; finance_source_id: number | null }) => Promise<void>;
  onSelectTreatmentOutcome: (treatment_outcome_id: number | null) => Promise<void>;
  onSelectCasesMonth: (month: number) => Promise<void>;
  onSelectCasesDay: (day: number) => Promise<void>;
  onSelectBedDaysYear: (year: number) => Promise<void>;
  onSelectBedDaysMonth: (month: number) => Promise<void>;
  onSelectBedProfileYear: (year: number) => Promise<void>;
  onSelectBedProfile: (bed_profile_id: number | null) => Promise<void>;
  onSelectBedProfileMonth: (month: number) => Promise<void>;
  onSelectMkb10Year: (year: number) => Promise<void>;
  onSelectMkb10: (mkb10_id: number | null) => Promise<void>;
  onSelectMkb10Month: (month: number) => Promise<void>;
  onSelectDepartmentYear: (year: number) => Promise<void>;
  onSelectDepartment: (department_id: number | null) => Promise<void>;
  onSelectDoctor: (performer_employee_id: string | null) => Promise<void>;
  onSelectDoctorMonth: (month: number) => Promise<void>;
}

export function useHospitalDashboard(options?: { mode?: HospitalDashboardMode }): UseHospitalDashboardResult {
  const mode: HospitalDashboardMode = options?.mode === "mother" ? "mother" : "standard";
  const initialFilters = useMemo(() => getInitialHospitalFiltersFromUrl(mode), [mode]);
  const [state, setState] = useState<HospitalDashboardState>(() => ({
    ...createInitialHospitalDashboardState(),
    filters: initialFilters,
  }));
  const actionRef = useRef(0);

  const startAction = useCallback(() => {
    actionRef.current += 1;
    return actionRef.current;
  }, []);

  const isCurrent = useCallback((actionId: number) => actionRef.current === actionId, []);

  const setLoading = useCallback((key: HospitalLoadingKey, value: boolean) => {
    setState((prev) => ({
      ...prev,
      loading: { ...prev.loading, [key]: value },
    }));
  }, []);

  const setError = useCallback((key: HospitalErrorKey, value: string | null) => {
    setState((prev) => ({
      ...prev,
      errors: { ...prev.errors, [key]: value },
    }));
  }, []);

  const setChart = useCallback(<K extends HospitalChartKey>(key: K, value: HospitalDashboardState["charts"][K]) => {
    setState((prev) => ({
      ...prev,
      charts: { ...prev.charts, [key]: value },
    }));
  }, []);

  const patchFilters = useCallback((patch: Partial<HospitalFilters>) => {
    setState((prev) => ({
      ...prev,
      filters: normalizeHospitalFiltersForMode({ ...prev.filters, ...patch }, mode),
    }));
  }, [mode]);

  const setFilter = useCallback(<K extends HospitalFilterKey>(key: K, value: HospitalFilters[K]) => {
    patchFilters({ [key]: value } as Partial<HospitalFilters>);
  }, [patchFilters]);

  const resetFilters = useCallback(() => {
    setState({
      filters: initialFilters,
      charts: createInitialHospitalChartsState(),
      loading: createInitialHospitalLoadingState(),
      errors: createInitialHospitalErrorState(),
    });
  }, [initialFilters]);

  const buildNextFilters = useCallback(
    (patch?: Partial<HospitalFilters>): HospitalFilters => ({
      ...normalizeHospitalFiltersForMode(
        {
          ...state.filters,
          ...(patch ?? {}),
        },
        mode,
      ),
    }),
    [mode, state.filters],
  );

  const clearBelowPlace = useCallback(() => {
    setState((prev) => ({
      ...prev,
      charts: {
        ...prev.charts,
        slide2_yearFunding: [],
        slide3_outcomeYear: [],
        slide4_bedDaysYear: [],
        slide41_bedDaysMonth: [],
        slide42_bedDaysDay: [],
        slide5_casesMonth: [],
        slide6_casesDay: [],
        slide7_bedProfileYear: [],
        slide71_bedProfileMonth: [],
        slide72_bedProfileDay: [],
        slide8_mkb10Year: [],
        slide81_mkb10Month: [],
        slide82_mkb10Day: [],
        slide9_departments: [],
        slide10_doctors: [],
        slide11_doctorMonth: [],
        slide12_doctorDay: [],
      },
      loading: {
        ...prev.loading,
        slide2_yearFunding: false,
        slide3_outcomeYear: false,
        slide4_bedDaysYear: false,
        slide41_bedDaysMonth: false,
        slide42_bedDaysDay: false,
        slide5_casesMonth: false,
        slide6_casesDay: false,
        slide7_bedProfileYear: false,
        slide71_bedProfileMonth: false,
        slide72_bedProfileDay: false,
        slide8_mkb10Year: false,
        slide81_mkb10Month: false,
        slide82_mkb10Day: false,
        slide9_departments: false,
        slide10_doctors: false,
        slide11_doctorMonth: false,
        slide12_doctorDay: false,
      },
      errors: {
        ...prev.errors,
        slide2_yearFunding: null,
        slide3_outcomeYear: null,
        slide4_bedDaysYear: null,
        slide41_bedDaysMonth: null,
        slide42_bedDaysDay: null,
        slide5_casesMonth: null,
        slide6_casesDay: null,
        slide7_bedProfileYear: null,
        slide71_bedProfileMonth: null,
        slide72_bedProfileDay: null,
        slide8_mkb10Year: null,
        slide81_mkb10Month: null,
        slide82_mkb10Day: null,
        slide9_departments: null,
        slide10_doctors: null,
        slide11_doctorMonth: null,
        slide12_doctorDay: null,
      },
    }));
  }, []);

  const loadBootstrap = useCallback(
    async (actionId: number, mis_id?: string | null, recent_years?: number | null) => {
      setLoading("bootstrap", true);
      setError("bootstrap", null);
      try {
        const bootstrap = await fetchHospitalBootstrap(mis_id, recent_years);
        if (!isCurrent(actionId)) return;
        const isSuperadmin = getStoredDashboardRole() === "SUPERADMIN";
        const nextMisId = mis_id ?? (isSuperadmin ? null : getDefaultMisId(bootstrap.available_orgs));
        setState((prev) => ({
          ...prev,
          charts: {
            ...prev.charts,
            bootstrap,
            slide1_place: bootstrap.place_chart,
            slide2_yearFunding: bootstrap.year_funding_chart,
          },
          filters: {
            ...normalizeHospitalFiltersForMode(prev.filters, mode),
            mis_id: nextMisId,
          },
          errors: { ...prev.errors, bootstrap: null },
        }));
        return bootstrap;
      } catch (error) {
        if (isCurrent(actionId)) setError("bootstrap", toErrorMessage(error));
        return null;
      } finally {
        if (isCurrent(actionId)) setLoading("bootstrap", false);
      }
    },
    [isCurrent, mode, setError, setLoading],
  );

  const loadYearFunding = useCallback(async (actionId: number, mis_id: string | null | undefined, place: HospitalPlace) => {
    setLoading("slide2_yearFunding", true);
    setError("slide2_yearFunding", null);
    try {
      const rows = await fetchHospitalYearFunding({
        mis_id: mis_id ?? undefined,
        place,
      });
      if (!isCurrent(actionId)) return;
      setChart("slide2_yearFunding", rows);
    } catch (error) {
      if (isCurrent(actionId)) setError("slide2_yearFunding", toErrorMessage(error));
      throw error;
    } finally {
      if (isCurrent(actionId)) setLoading("slide2_yearFunding", false);
    }
  }, [isCurrent, setChart, setError, setLoading]);

  const loadOutcomeYear = useCallback(async (actionId: number, filters: HospitalFilters) => {
    setLoading("slide3_outcomeYear", true);
    setError("slide3_outcomeYear", null);
    try {
      const rows = await fetchHospitalOutcomeYear({
        mis_id: filters.mis_id ?? undefined,
        place: filters.place,
        finance_source_id: filters.finance_source_id,
        recent_years: filters.place ? undefined : INITIAL_RECENT_YEARS,
      });
      if (!isCurrent(actionId)) return;
      setChart("slide3_outcomeYear", rows);
    } catch (error) {
      if (isCurrent(actionId)) setError("slide3_outcomeYear", toErrorMessage(error));
      throw error;
    } finally {
      if (isCurrent(actionId)) setLoading("slide3_outcomeYear", false);
    }
  }, [isCurrent, setChart, setError, setLoading]);

  const loadBedDaysYear = useCallback(async (actionId: number, filters: HospitalFilters) => {
    setLoading("slide4_bedDaysYear", true);
    setError("slide4_bedDaysYear", null);
    try {
      const rows = await fetchHospitalBedDaysYear({
        mis_id: filters.mis_id ?? undefined,
        place: filters.place,
        finance_source_id: filters.finance_source_id,
        treatment_outcome_id: filters.treatment_outcome_id,
        recent_years: filters.place ? undefined : INITIAL_RECENT_YEARS,
      });
      if (!isCurrent(actionId)) return;
      setChart("slide4_bedDaysYear", rows);
    } catch (error) {
      if (isCurrent(actionId)) setError("slide4_bedDaysYear", toErrorMessage(error));
      throw error;
    } finally {
      if (isCurrent(actionId)) setLoading("slide4_bedDaysYear", false);
    }
  }, [isCurrent, setChart, setError, setLoading]);

  const loadBedDaysMonth = useCallback(async (actionId: number, filters: HospitalFilters) => {
    if (!filters.mis_id || !filters.place || filters.year_b === null) {
      setChart("slide41_bedDaysMonth", []);
      setChart("slide42_bedDaysDay", []);
      return;
    }
    setLoading("slide41_bedDaysMonth", true);
    setError("slide41_bedDaysMonth", null);
    try {
      const rows = await fetchHospitalBedDaysMonth({ mis_id: filters.mis_id, place: filters.place, year: filters.year_b, finance_source_id: filters.finance_source_id, treatment_outcome_id: filters.treatment_outcome_id });
      if (!isCurrent(actionId)) return;
      setChart("slide41_bedDaysMonth", rows);
      setChart("slide42_bedDaysDay", []);
    } catch (error) {
      if (isCurrent(actionId)) setError("slide41_bedDaysMonth", toErrorMessage(error));
      throw error;
    } finally {
      if (isCurrent(actionId)) setLoading("slide41_bedDaysMonth", false);
    }
  }, [isCurrent, setChart, setError, setLoading]);

  const loadBedDaysDay = useCallback(async (actionId: number, filters: HospitalFilters) => {
    if (!filters.mis_id || !filters.place || filters.year_b === null || filters.month_b === null) {
      setChart("slide42_bedDaysDay", []);
      return;
    }
    setLoading("slide42_bedDaysDay", true);
    setError("slide42_bedDaysDay", null);
    try {
      const rows = await fetchHospitalBedDaysDay({ mis_id: filters.mis_id, place: filters.place, year: filters.year_b, month: filters.month_b, finance_source_id: filters.finance_source_id, treatment_outcome_id: filters.treatment_outcome_id });
      if (!isCurrent(actionId)) return;
      setChart("slide42_bedDaysDay", rows);
    } catch (error) {
      if (isCurrent(actionId)) setError("slide42_bedDaysDay", toErrorMessage(error));
      throw error;
    } finally {
      if (isCurrent(actionId)) setLoading("slide42_bedDaysDay", false);
    }
  }, [isCurrent, setChart, setError, setLoading]);

  const loadCasesMonth = useCallback(async (actionId: number, filters: HospitalFilters) => {
    setLoading("slide5_casesMonth", true);
    setError("slide5_casesMonth", null);
    try {
      const rows = await fetchHospitalCasesMonth({
        mis_id: filters.mis_id ?? undefined,
        place: filters.place,
        year: filters.year,
        finance_source_id: filters.finance_source_id,
        treatment_outcome_id: filters.treatment_outcome_id,
        recent_years: filters.year === null ? INITIAL_RECENT_YEARS : undefined,
      });
      if (!isCurrent(actionId)) return;
      setChart("slide5_casesMonth", rows);
      setChart("slide6_casesDay", []);
    } catch (error) {
      if (isCurrent(actionId)) setError("slide5_casesMonth", toErrorMessage(error));
      throw error;
    } finally {
      if (isCurrent(actionId)) setLoading("slide5_casesMonth", false);
    }
  }, [isCurrent, setChart, setError, setLoading]);

  const loadCasesDay = useCallback(async (actionId: number, filters: HospitalFilters) => {
    if (!filters.mis_id || !filters.place || filters.year === null || filters.month === null) {
      setChart("slide6_casesDay", []);
      return;
    }
    setLoading("slide6_casesDay", true);
    setError("slide6_casesDay", null);
    try {
      const rows = await fetchHospitalCasesDay({ mis_id: filters.mis_id, place: filters.place, year: filters.year, month: filters.month, finance_source_id: filters.finance_source_id, treatment_outcome_id: filters.treatment_outcome_id });
      if (!isCurrent(actionId)) return;
      setChart("slide6_casesDay", rows);
    } catch (error) {
      if (isCurrent(actionId)) setError("slide6_casesDay", toErrorMessage(error));
      throw error;
    } finally {
      if (isCurrent(actionId)) setLoading("slide6_casesDay", false);
    }
  }, [isCurrent, setChart, setError, setLoading]);

  const loadBedProfileYear = useCallback(async (actionId: number, filters: HospitalFilters) => {
    setLoading("slide7_bedProfileYear", true);
    setError("slide7_bedProfileYear", null);
    try {
      const rows = await fetchHospitalBedProfileYear({
        mis_id: filters.mis_id ?? undefined,
        place: filters.place,
        year: filters.year_p,
        finance_source_id: filters.finance_source_id,
        treatment_outcome_id: filters.treatment_outcome_id,
        recent_years: filters.year_p === null ? INITIAL_RECENT_YEARS : undefined,
      });
      if (!isCurrent(actionId)) return;
      setChart("slide7_bedProfileYear", rows);
      setChart("slide71_bedProfileMonth", []);
      setChart("slide72_bedProfileDay", []);
    } catch (error) {
      if (isCurrent(actionId)) setError("slide7_bedProfileYear", toErrorMessage(error));
      throw error;
    } finally {
      if (isCurrent(actionId)) setLoading("slide7_bedProfileYear", false);
    }
  }, [isCurrent, setChart, setError, setLoading]);

  const loadBedProfileMonth = useCallback(async (actionId: number, filters: HospitalFilters) => {
    if (!filters.mis_id || !filters.place || filters.year_p === null || filters.bed_profile_id === null) {
      setChart("slide71_bedProfileMonth", []);
      setChart("slide72_bedProfileDay", []);
      return;
    }
    setLoading("slide71_bedProfileMonth", true);
    setError("slide71_bedProfileMonth", null);
    try {
      const rows = await fetchHospitalBedProfileMonth({ mis_id: filters.mis_id, place: filters.place, year: filters.year_p, bed_profile_id: filters.bed_profile_id, finance_source_id: filters.finance_source_id, treatment_outcome_id: filters.treatment_outcome_id });
      if (!isCurrent(actionId)) return;
      setChart("slide71_bedProfileMonth", rows);
      setChart("slide72_bedProfileDay", []);
    } catch (error) {
      if (isCurrent(actionId)) setError("slide71_bedProfileMonth", toErrorMessage(error));
      throw error;
    } finally {
      if (isCurrent(actionId)) setLoading("slide71_bedProfileMonth", false);
    }
  }, [isCurrent, setChart, setError, setLoading]);

  const loadBedProfileDay = useCallback(async (actionId: number, filters: HospitalFilters) => {
    if (!filters.mis_id || !filters.place || filters.year_p === null || filters.bed_profile_id === null || filters.month_p === null) {
      setChart("slide72_bedProfileDay", []);
      return;
    }
    setLoading("slide72_bedProfileDay", true);
    setError("slide72_bedProfileDay", null);
    try {
      const rows = await fetchHospitalBedProfileDay({ mis_id: filters.mis_id, place: filters.place, year: filters.year_p, month: filters.month_p, bed_profile_id: filters.bed_profile_id, finance_source_id: filters.finance_source_id, treatment_outcome_id: filters.treatment_outcome_id });
      if (!isCurrent(actionId)) return;
      setChart("slide72_bedProfileDay", rows);
    } catch (error) {
      if (isCurrent(actionId)) setError("slide72_bedProfileDay", toErrorMessage(error));
      throw error;
    } finally {
      if (isCurrent(actionId)) setLoading("slide72_bedProfileDay", false);
    }
  }, [isCurrent, setChart, setError, setLoading]);

  const loadMkb10Year = useCallback(async (actionId: number, filters: HospitalFilters) => {
    setLoading("slide8_mkb10Year", true);
    setError("slide8_mkb10Year", null);
    try {
      const rows = await fetchHospitalMkb10Year({
        mis_id: filters.mis_id ?? undefined,
        place: filters.place,
        year: filters.year_m,
        finance_source_id: filters.finance_source_id,
        treatment_outcome_id: filters.treatment_outcome_id,
        recent_years: filters.year_m === null ? INITIAL_RECENT_YEARS : undefined,
      });
      if (!isCurrent(actionId)) return;
      setChart("slide8_mkb10Year", rows);
      setChart("slide81_mkb10Month", []);
      setChart("slide82_mkb10Day", []);
    } catch (error) {
      if (isCurrent(actionId)) setError("slide8_mkb10Year", toErrorMessage(error));
      throw error;
    } finally {
      if (isCurrent(actionId)) setLoading("slide8_mkb10Year", false);
    }
  }, [isCurrent, setChart, setError, setLoading]);

  const loadMkb10Month = useCallback(async (actionId: number, filters: HospitalFilters) => {
    if (!filters.mis_id || !filters.place || filters.year_m === null || filters.mkb10_id === null) {
      setChart("slide81_mkb10Month", []);
      setChart("slide82_mkb10Day", []);
      return;
    }
    setLoading("slide81_mkb10Month", true);
    setError("slide81_mkb10Month", null);
    try {
      const rows = await fetchHospitalMkb10Month({ mis_id: filters.mis_id, place: filters.place, year: filters.year_m, mkb10_id: filters.mkb10_id, finance_source_id: filters.finance_source_id, treatment_outcome_id: filters.treatment_outcome_id });
      if (!isCurrent(actionId)) return;
      setChart("slide81_mkb10Month", rows);
      setChart("slide82_mkb10Day", []);
    } catch (error) {
      if (isCurrent(actionId)) setError("slide81_mkb10Month", toErrorMessage(error));
      throw error;
    } finally {
      if (isCurrent(actionId)) setLoading("slide81_mkb10Month", false);
    }
  }, [isCurrent, setChart, setError, setLoading]);

  const loadMkb10Day = useCallback(async (actionId: number, filters: HospitalFilters) => {
    if (!filters.mis_id || !filters.place || filters.year_m === null || filters.mkb10_id === null || filters.month_m === null) {
      setChart("slide82_mkb10Day", []);
      return;
    }
    setLoading("slide82_mkb10Day", true);
    setError("slide82_mkb10Day", null);
    try {
      const rows = await fetchHospitalMkb10Day({ mis_id: filters.mis_id, place: filters.place, year: filters.year_m, month: filters.month_m, mkb10_id: filters.mkb10_id, finance_source_id: filters.finance_source_id, treatment_outcome_id: filters.treatment_outcome_id });
      if (!isCurrent(actionId)) return;
      setChart("slide82_mkb10Day", rows);
    } catch (error) {
      if (isCurrent(actionId)) setError("slide82_mkb10Day", toErrorMessage(error));
      throw error;
    } finally {
      if (isCurrent(actionId)) setLoading("slide82_mkb10Day", false);
    }
  }, [isCurrent, setChart, setError, setLoading]);

  const loadDepartments = useCallback(async (actionId: number, filters: HospitalFilters) => {
    setLoading("slide9_departments", true);
    setError("slide9_departments", null);
    try {
      const rows = await fetchHospitalDepartments({
        mis_id: filters.mis_id ?? undefined,
        place: filters.place,
        year: filters.year_d,
        finance_source_id: filters.finance_source_id,
        treatment_outcome_id: filters.treatment_outcome_id,
        recent_years: filters.year_d === null ? INITIAL_RECENT_YEARS : undefined,
      });
      if (!isCurrent(actionId)) return;
      setChart("slide9_departments", rows);
      setChart("slide10_doctors", []);
      setChart("slide11_doctorMonth", []);
      setChart("slide12_doctorDay", []);
    } catch (error) {
      if (isCurrent(actionId)) setError("slide9_departments", toErrorMessage(error));
      throw error;
    } finally {
      if (isCurrent(actionId)) setLoading("slide9_departments", false);
    }
  }, [isCurrent, setChart, setError, setLoading]);

  const loadDoctors = useCallback(async (actionId: number, filters: HospitalFilters) => {
    if (!filters.mis_id || !filters.place || filters.year_d === null || filters.department_id === null) {
      setChart("slide10_doctors", []);
      setChart("slide11_doctorMonth", []);
      setChart("slide12_doctorDay", []);
      return;
    }
    setLoading("slide10_doctors", true);
    setError("slide10_doctors", null);
    try {
      const rows = await fetchHospitalDoctors({ mis_id: filters.mis_id, place: filters.place, year: filters.year_d, department_id: filters.department_id, finance_source_id: filters.finance_source_id, treatment_outcome_id: filters.treatment_outcome_id });
      if (!isCurrent(actionId)) return;
      setChart("slide10_doctors", rows);
      setChart("slide11_doctorMonth", []);
      setChart("slide12_doctorDay", []);
    } catch (error) {
      if (isCurrent(actionId)) setError("slide10_doctors", toErrorMessage(error));
      throw error;
    } finally {
      if (isCurrent(actionId)) setLoading("slide10_doctors", false);
    }
  }, [isCurrent, setChart, setError, setLoading]);

  const loadDoctorMonth = useCallback(async (actionId: number, filters: HospitalFilters) => {
    if (!filters.mis_id || !filters.place || filters.year_d === null || filters.department_id === null || filters.performer_employee_id === null) {
      setChart("slide11_doctorMonth", []);
      setChart("slide12_doctorDay", []);
      return;
    }
    setLoading("slide11_doctorMonth", true);
    setError("slide11_doctorMonth", null);
    try {
      const rows = await fetchHospitalDoctorMonth({ mis_id: filters.mis_id, place: filters.place, year: filters.year_d, department_id: filters.department_id, performer_employee_id: filters.performer_employee_id, finance_source_id: filters.finance_source_id, treatment_outcome_id: filters.treatment_outcome_id });
      if (!isCurrent(actionId)) return;
      setChart("slide11_doctorMonth", rows);
      setChart("slide12_doctorDay", []);
    } catch (error) {
      if (isCurrent(actionId)) setError("slide11_doctorMonth", toErrorMessage(error));
      throw error;
    } finally {
      if (isCurrent(actionId)) setLoading("slide11_doctorMonth", false);
    }
  }, [isCurrent, setChart, setError, setLoading]);

  const loadDoctorDay = useCallback(async (actionId: number, filters: HospitalFilters) => {
    if (!filters.mis_id || !filters.place || filters.year_d === null || filters.department_id === null || filters.performer_employee_id === null || filters.month_d === null) {
      setChart("slide12_doctorDay", []);
      return;
    }
    setLoading("slide12_doctorDay", true);
    setError("slide12_doctorDay", null);
    try {
      const rows = await fetchHospitalDoctorDay({ mis_id: filters.mis_id, place: filters.place, year: filters.year_d, month: filters.month_d, department_id: filters.department_id, performer_employee_id: filters.performer_employee_id, finance_source_id: filters.finance_source_id, treatment_outcome_id: filters.treatment_outcome_id });
      if (!isCurrent(actionId)) return;
      setChart("slide12_doctorDay", rows);
    } catch (error) {
      if (isCurrent(actionId)) setError("slide12_doctorDay", toErrorMessage(error));
      throw error;
    } finally {
      if (isCurrent(actionId)) setLoading("slide12_doctorDay", false);
    }
  }, [isCurrent, setChart, setError, setLoading]);

  const loadTopLevelAfterYearSelection = useCallback(async (actionId: number, filters: HospitalFilters) => {
    await Promise.all([
      loadOutcomeYear(actionId, filters),
      loadBedDaysYear(actionId, filters),
      loadCasesMonth(actionId, filters),
      loadBedProfileYear(actionId, filters),
      loadMkb10Year(actionId, filters),
      loadDepartments(actionId, filters),
    ]);
  }, [loadBedDaysYear, loadBedProfileYear, loadCasesMonth, loadDepartments, loadMkb10Year, loadOutcomeYear]);

  const loadInitialOverview = useCallback(async (actionId: number, filters: HospitalFilters) => {
    const overviewFilters: HospitalFilters = {
      ...filters,
      finance_source_id: null,
      treatment_outcome_id: null,
      year: null,
      month: null,
      day: null,
      year_b: null,
      month_b: null,
      year_p: null,
      bed_profile_id: null,
      month_p: null,
      year_m: null,
      mkb10_id: null,
      month_m: null,
      year_d: null,
      department_id: null,
      performer_employee_id: null,
      month_d: null,
    };

    await Promise.all([
      loadOutcomeYear(actionId, overviewFilters),
      loadBedDaysYear(actionId, overviewFilters),
      loadCasesMonth(actionId, overviewFilters),
      loadBedProfileYear(actionId, overviewFilters),
      loadMkb10Year(actionId, overviewFilters),
      loadDepartments(actionId, overviewFilters),
    ]);

    if (!isCurrent(actionId)) return;

    setChart("slide41_bedDaysMonth", []);
    setChart("slide42_bedDaysDay", []);
    setChart("slide6_casesDay", []);
    setChart("slide71_bedProfileMonth", []);
    setChart("slide72_bedProfileDay", []);
    setChart("slide81_mkb10Month", []);
    setChart("slide82_mkb10Day", []);
    setChart("slide10_doctors", []);
    setChart("slide11_doctorMonth", []);
    setChart("slide12_doctorDay", []);
  }, [
    isCurrent,
    loadBedDaysYear,
    loadBedProfileYear,
    loadCasesMonth,
    loadDepartments,
    loadMkb10Year,
    loadOutcomeYear,
    setChart,
  ]);
  const reloadAllVisibleData = useCallback(async (override?: Partial<HospitalFilters>) => {
    const actionId = startAction();
    const filters = buildNextFilters(override);

    await loadBootstrap(actionId, filters.mis_id, INITIAL_RECENT_YEARS);

    // Нет выбранного place -> общий overview
    if (!filters.place) {
      await loadInitialOverview(actionId, filters);
      return;
    }

    // Place выбран -> Сл2 грузим всегда
    await loadYearFunding(actionId, filters.mis_id ?? undefined, filters.place);

    // Для стационара сразу грузим общий обзор за последние годы,
    // а детализацию оставляем на выбор year + finance_source_id.
    if (!filters.mis_id) {
      await loadInitialOverview(actionId, filters);
      return;
    }

    await loadInitialOverview(actionId, filters);

    // МО выбрана и уже есть год + источник финансирования -> грузим детализацию.
    if (filters.finance_source_id !== null && filters.year !== null) {
      await loadTopLevelAfterYearSelection(actionId, filters);
    }

    if (filters.year_b !== null) await loadBedDaysMonth(actionId, filters);
    if (filters.month_b !== null) await loadBedDaysDay(actionId, filters);
    if (filters.month !== null) await loadCasesDay(actionId, filters);
    if (filters.bed_profile_id !== null) await loadBedProfileMonth(actionId, filters);
    if (filters.month_p !== null) await loadBedProfileDay(actionId, filters);
    if (filters.mkb10_id !== null) await loadMkb10Month(actionId, filters);
    if (filters.month_m !== null) await loadMkb10Day(actionId, filters);
    if (filters.department_id !== null) await loadDoctors(actionId, filters);
    if (filters.performer_employee_id !== null) await loadDoctorMonth(actionId, filters);
    if (filters.month_d !== null) await loadDoctorDay(actionId, filters);
  }, [
    buildNextFilters,
    loadBedDaysDay,
    loadBedDaysMonth,
    loadBedProfileDay,
    loadBedProfileMonth,
    loadBootstrap,
    loadCasesDay,
    loadDoctorDay,
    loadDoctorMonth,
    loadDoctors,
    loadInitialOverview,
    loadMkb10Day,
    loadMkb10Month,
    loadTopLevelAfterYearSelection,
    loadYearFunding,
    setChart,
    startAction,
  ]);
  const reloadBootstrap = useCallback(async () => {
    const actionId = startAction();
    await loadBootstrap(actionId, state.filters.mis_id, INITIAL_RECENT_YEARS);
  }, [loadBootstrap, startAction, state.filters.mis_id]);

  const onSelectMisId = useCallback(async (mis_id: string) => {
    const actionId = startAction();
    patchFilters({
      mis_id,
      place: null,
      finance_source_id: null,
      treatment_outcome_id: null,
      year: null,
      month: null,
      day: null,

      year_b: null,
      month_b: null,

      year_p: null,
      bed_profile_id: null,
      month_p: null,

      year_m: null,
      mkb10_id: null,
      month_m: null,

      year_d: null,
      department_id: null,
      performer_employee_id: null,
      month_d: null,
    });
    clearBelowPlace();
    await loadBootstrap(actionId, mis_id, INITIAL_RECENT_YEARS);
  }, [clearBelowPlace, loadBootstrap, patchFilters, startAction]);

  const onSelectPlace = useCallback(async (place: HospitalPlace) => {
    const actionId = startAction();
    const nextFilters: Partial<HospitalFilters> = {
      place,
      finance_source_id: null,
      treatment_outcome_id: null,
      year: null,
      month: null,
      day: null,
      year_b: null,
      month_b: null,
      year_p: null,
      bed_profile_id: null,
      month_p: null,
      year_m: null,
      mkb10_id: null,
      month_m: null,
      year_d: null,
      department_id: null,
      performer_employee_id: null,
      month_d: null,
    };

    patchFilters(nextFilters);
    clearBelowPlace();

    const filters = buildNextFilters(nextFilters);

    await loadYearFunding(actionId, filters.mis_id ?? undefined, place);
    await loadInitialOverview(actionId, filters);
  }, [
    buildNextFilters,
    clearBelowPlace,
    loadInitialOverview,
    loadYearFunding,
    patchFilters,
    startAction,
  ]);

  const onSelectFinanceSource = useCallback(async (finance_source_id: number | null) => {
    const actionId = startAction();
    const nextFilters: Partial<HospitalFilters> = {
      finance_source_id,
      treatment_outcome_id: null,
      month: null,
      day: null,
      month_b: null,
      year_p: state.filters.year,
      bed_profile_id: null,
      month_p: null,
      mkb10_id: null,
      month_m: null,
      department_id: null,
      performer_employee_id: null,
      month_d: null,
    };
    patchFilters(nextFilters);
    const filters = buildNextFilters(nextFilters);
    if (filters.year !== null && filters.finance_source_id !== null) {
      await loadTopLevelAfterYearSelection(actionId, filters);
    }
  }, [buildNextFilters, loadTopLevelAfterYearSelection, patchFilters, startAction, state.filters.year]);

  const onSelectYearFunding = useCallback(
    async (params: { year: number; finance_source_id: number | null }) => {
      const actionId = startAction();

      const nextFilters: Partial<HospitalFilters> = {
        finance_source_id: params.finance_source_id,
        year: params.year,
        year_p: params.year,

        treatment_outcome_id: null,

        month: null,
        day: null,

        year_b: null,
        month_b: null,

        bed_profile_id: null,
        month_p: null,

        year_m: null,
        mkb10_id: null,
        month_m: null,

        year_d: null,
        department_id: null,
        performer_employee_id: null,
        month_d: null,
      };

      patchFilters(nextFilters);

      const filters = buildNextFilters(nextFilters);
      await loadTopLevelAfterYearSelection(actionId, filters);
    },
    [buildNextFilters, loadTopLevelAfterYearSelection, patchFilters, startAction],
  );

  const onSelectTreatmentOutcome = useCallback(async (treatment_outcome_id: number | null) => {
    const actionId = startAction();
    const nextFilters: Partial<HospitalFilters> = {
      treatment_outcome_id,
      month: null,
      day: null,
      month_b: null,
      bed_profile_id: null,
      month_p: null,
      mkb10_id: null,
      month_m: null,
      department_id: null,
      performer_employee_id: null,
      month_d: null,
    };
    patchFilters(nextFilters);
    const filters = buildNextFilters(nextFilters);
    await Promise.all([
      loadBedDaysYear(actionId, filters),
      loadCasesMonth(actionId, filters),
      loadBedProfileYear(actionId, filters),
      loadMkb10Year(actionId, filters),
      loadDepartments(actionId, filters),
    ]);
  }, [buildNextFilters, loadBedDaysYear, loadBedProfileYear, loadCasesMonth, loadDepartments, loadMkb10Year, patchFilters, startAction]);

  const onSelectCasesMonth = useCallback(async (month: number) => {
    const actionId = startAction();
    const nextFilters: Partial<HospitalFilters> = { month, day: null };
    patchFilters(nextFilters);
    await loadCasesDay(actionId, buildNextFilters(nextFilters));
  }, [buildNextFilters, loadCasesDay, patchFilters, startAction]);

  const onSelectCasesDay = useCallback(async (day: number) => {
    patchFilters({ day });
  }, [patchFilters]);

  const onSelectBedDaysYear = useCallback(async (year: number) => {
    const actionId = startAction();
    const nextFilters: Partial<HospitalFilters> = { year_b: year, month_b: null };
    patchFilters(nextFilters);
    await loadBedDaysMonth(actionId, buildNextFilters(nextFilters));
  }, [buildNextFilters, loadBedDaysMonth, patchFilters, startAction]);

  const onSelectBedDaysMonth = useCallback(async (month: number) => {
    const actionId = startAction();
    const nextFilters: Partial<HospitalFilters> = { month_b: month };
    patchFilters(nextFilters);
    await loadBedDaysDay(actionId, buildNextFilters(nextFilters));
  }, [buildNextFilters, loadBedDaysDay, patchFilters, startAction]);

  const onSelectBedProfileYear = useCallback(async (year: number) => {
    const actionId = startAction();
    const nextFilters: Partial<HospitalFilters> = { year_p: year, bed_profile_id: null, month_p: null };
    patchFilters(nextFilters);
    await loadBedProfileYear(actionId, buildNextFilters(nextFilters));
  }, [buildNextFilters, loadBedProfileYear, patchFilters, startAction]);

  const onSelectBedProfile = useCallback(async (bed_profile_id: number | null) => {
    const actionId = startAction();
    const fallbackYear = state.filters.year ?? getLatestFundingYear(state.charts.slide2_yearFunding);
    const nextFilters: Partial<HospitalFilters> = { bed_profile_id, month_p: null };
    if (fallbackYear !== null) {
      nextFilters.year_p = state.filters.year_p ?? fallbackYear;
    }
    patchFilters(nextFilters);
    await loadBedProfileMonth(actionId, buildNextFilters(nextFilters));
  }, [buildNextFilters, loadBedProfileMonth, patchFilters, startAction, state.charts.slide2_yearFunding, state.filters.year, state.filters.year_p]);

  const onSelectBedProfileMonth = useCallback(async (month: number) => {
    const actionId = startAction();
    const nextFilters: Partial<HospitalFilters> = { month_p: month };
    patchFilters(nextFilters);
    await loadBedProfileDay(actionId, buildNextFilters(nextFilters));
  }, [buildNextFilters, loadBedProfileDay, patchFilters, startAction]);

  const onSelectMkb10Year = useCallback(async (year: number) => {
    const actionId = startAction();
    const nextFilters: Partial<HospitalFilters> = { year_m: year, mkb10_id: null, month_m: null };
    patchFilters(nextFilters);
    await loadMkb10Year(actionId, buildNextFilters(nextFilters));
  }, [buildNextFilters, loadMkb10Year, patchFilters, startAction]);

  const onSelectMkb10 = useCallback(async (mkb10_id: number | null) => {
    const actionId = startAction();
    const fallbackYear = state.filters.year ?? getLatestFundingYear(state.charts.slide2_yearFunding);
    const nextFilters: Partial<HospitalFilters> = { mkb10_id, month_m: null };
    if (fallbackYear !== null) {
      nextFilters.year_m = state.filters.year_m ?? fallbackYear;
    }
    patchFilters(nextFilters);
    await loadMkb10Month(actionId, buildNextFilters(nextFilters));
  }, [buildNextFilters, loadMkb10Month, patchFilters, startAction, state.charts.slide2_yearFunding, state.filters.year, state.filters.year_m]);

  const onSelectMkb10Month = useCallback(async (month: number) => {
    const actionId = startAction();
    const nextFilters: Partial<HospitalFilters> = { month_m: month };
    patchFilters(nextFilters);
    await loadMkb10Day(actionId, buildNextFilters(nextFilters));
  }, [buildNextFilters, loadMkb10Day, patchFilters, startAction]);

  const onSelectDepartmentYear = useCallback(async (year: number) => {
    const actionId = startAction();
    const nextFilters: Partial<HospitalFilters> = { year_d: year, department_id: null, performer_employee_id: null, month_d: null };
    patchFilters(nextFilters);
    await loadDepartments(actionId, buildNextFilters(nextFilters));
  }, [buildNextFilters, loadDepartments, patchFilters, startAction]);

  const onSelectDepartment = useCallback(async (department_id: number | null) => {
    const actionId = startAction();
    const fallbackYear = state.filters.year ?? getLatestFundingYear(state.charts.slide2_yearFunding);
    const nextFilters: Partial<HospitalFilters> = { department_id, performer_employee_id: null, month_d: null };
    if (fallbackYear !== null) {
      nextFilters.year_d = state.filters.year_d ?? fallbackYear;
    }
    patchFilters(nextFilters);
    await loadDoctors(actionId, buildNextFilters(nextFilters));
  }, [buildNextFilters, loadDoctors, patchFilters, startAction, state.charts.slide2_yearFunding, state.filters.year, state.filters.year_d]);

  const onSelectDoctor = useCallback(async (performer_employee_id: string | null) => {
    const actionId = startAction();
    const nextFilters: Partial<HospitalFilters> = { performer_employee_id, month_d: null };
    patchFilters(nextFilters);
    await loadDoctorMonth(actionId, buildNextFilters(nextFilters));
  }, [buildNextFilters, loadDoctorMonth, patchFilters, startAction]);

  const onSelectDoctorMonth = useCallback(async (month: number) => {
    const actionId = startAction();
    const nextFilters: Partial<HospitalFilters> = { month_d: month };
    patchFilters(nextFilters);
    await loadDoctorDay(actionId, buildNextFilters(nextFilters));
  }, [buildNextFilters, loadDoctorDay, patchFilters, startAction]);

  const resetFiltersAndReload = useCallback(async () => {
    const actionId = startAction();
    const resetFilters = {
      ...initialFilters,
      mis_id: state.filters.mis_id,
    };

    setState((prev) => ({
      ...prev,
      filters: resetFilters,
      errors: createInitialHospitalErrorState(),
      loading: { ...prev.loading, bootstrap: true },
    }));

    clearBelowPlace();
    await loadBootstrap(actionId, resetFilters.mis_id, INITIAL_RECENT_YEARS);
  }, [clearBelowPlace, initialFilters, loadBootstrap, startAction, state.filters.mis_id]);

  useEffect(() => {
    const actionId = startAction();

    setState((prev) => ({
      ...prev,
      filters: initialFilters,
    }));

    void (async () => {
      const bootstrap = await loadBootstrap(actionId, initialFilters.mis_id ?? undefined, INITIAL_RECENT_YEARS);
      const isSuperadmin = getStoredDashboardRole() === "SUPERADMIN";
      const effectiveMisId = initialFilters.mis_id ?? (isSuperadmin ? null : getDefaultMisId(bootstrap?.available_orgs ?? []));
      const nextFilters: HospitalFilters = {
        ...initialFilters,
        mis_id: effectiveMisId,
      };

      if (nextFilters.place) {
        await loadYearFunding(actionId, nextFilters.mis_id ?? undefined, nextFilters.place);
      }

      await loadInitialOverview(actionId, nextFilters);
    })();
  }, [initialFilters, loadBootstrap, loadInitialOverview, loadYearFunding, startAction]);

  return useMemo<UseHospitalDashboardResult>(() => ({
    state,
    filters: state.filters,
    charts: state.charts,
    loading: state.loading,
    errors: state.errors,
    setFilter,
    patchFilters,
    resetFilters,
    resetFiltersAndReload,
    reloadBootstrap,
    reloadAllVisibleData,
    onSelectMisId,
    onSelectPlace,
    onSelectFinanceSource,
    onSelectYearFunding,
    onSelectTreatmentOutcome,
    onSelectCasesMonth,
    onSelectCasesDay,
    onSelectBedDaysYear,
    onSelectBedDaysMonth,
    onSelectBedProfileYear,
    onSelectBedProfile,
    onSelectBedProfileMonth,
    onSelectMkb10Year,
    onSelectMkb10,
    onSelectMkb10Month,
    onSelectDepartmentYear,
    onSelectDepartment,
    onSelectDoctor,
    onSelectDoctorMonth,
  }), [
    state,
    onSelectBedDaysMonth,
    onSelectBedDaysYear,
    onSelectBedProfile,
    onSelectBedProfileMonth,
    onSelectBedProfileYear,
    onSelectCasesMonth,
    onSelectCasesDay,
    onSelectDepartment,
    onSelectDepartmentYear,
    onSelectDoctor,
    onSelectDoctorMonth,
    onSelectFinanceSource,
    onSelectMkb10,
    onSelectMkb10Month,
    onSelectMkb10Year,
    onSelectMisId,
    onSelectPlace,
    onSelectTreatmentOutcome,
    onSelectYearFunding,
    patchFilters,
    reloadAllVisibleData,
    reloadBootstrap,
    resetFilters,
    resetFiltersAndReload,
    setFilter,
    state,
  ]);
}
